import React, { useState, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

const FeedbackWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim() || !message.trim()) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await axios.post('/api/feedback', {
        email: email.trim(),
        message: message.trim()
      });

      setSuccess(true);
      setEmail('');
      setMessage('');
      
      // Close modal after 2 seconds
      setTimeout(() => {
        setIsOpen(false);
        setSuccess(false);
      }, 2000);
      
    } catch (err) {
      // Enhanced error logging
      console.error('Feedback submission error:', {
        status: err.response?.status,
        data: err.response?.data,
        message: err.message,
        code: err.response?.data?.code
      });
      
      const errorMessage = err.response?.data?.error || 'Failed to send feedback. Please try again.';
      const errorCode = err.response?.data?.code;
      
      // Provide more specific error messages based on error code
      let userMessage = errorMessage;
      if (errorCode === 'SMTP_AUTH_ERROR') {
        userMessage = 'Email service is currently unavailable. Please try again later.';
      } else if (errorCode === 'SMTP_CONNECT_ERROR') {
        userMessage = 'Unable to connect to email service. Please check your connection and try again.';
      }
      
      setError(userMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setIsOpen(false);
      setError('');
      setSuccess(false);
    }
  };

  return (
    <Fragment>
      {/* Floating Trigger Button */}
      <motion.button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1, duration: 0.3, ease: 'easeOut' }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <div className="w-5 h-5 flex items-center justify-center">
          <MessageCircle className="w-5 h-5" />
        </div>
        <span className="font-medium text-sm">Feedback</span>
        
        {/* Subtle glow effect */}
        <div className="absolute inset-0 rounded-full bg-emerald-400/20 blur-lg scale-110 opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10" />
      </motion.button>

      {/* Modal Popup */}
      <AnimatePresence>
        {isOpen && (
          <Fragment>
            {/* Backdrop */}
            <motion.div
              className="fixed inset-0 bg-black/50 z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={handleClose}
            />

            {/* Modal */}
            <motion.div
              className="fixed bottom-24 right-6 z-50 w-96 max-w-[calc(100vw-3rem)]"
              initial={{ scale: 0.8, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 20 }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            >
              <div className="bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-zinc-800">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                      <MessageCircle className="w-4 h-4 text-emerald-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-zinc-100">Send Feedback</h3>
                  </div>
                  <button
                    onClick={handleClose}
                    className="w-8 h-8 rounded-lg hover:bg-zinc-800 flex items-center justify-center transition-colors"
                    disabled={loading}
                  >
                    <X className="w-4 h-4 text-zinc-400" />
                  </button>
                </div>

                {/* Content */}
                <div className="p-4">
                  {success ? (
                    <motion.div
                      className="text-center py-8"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                    >
                      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-500/20 flex items-center justify-center">
                        <CheckCircle className="w-8 h-8 text-emerald-400" />
                      </div>
                      <h4 className="text-xl font-semibold text-zinc-100 mb-2">
                        Thank you!
                      </h4>
                      <p className="text-zinc-400">
                        Your feedback has been sent successfully.
                      </p>
                    </motion.div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-4">
                      {/* Error Message */}
                      {error && (
                        <motion.div
                          className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-3"
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                        >
                          <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                          <p className="text-red-400 text-sm">{error}</p>
                        </motion.div>
                      )}

                      {/* Email Field */}
                      <div>
                        <label htmlFor="feedback-email" className="block text-sm font-medium text-zinc-300 mb-2">
                          Email Address
                        </label>
                        <input
                          id="feedback-email"
                          name="feedback-email"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="your@email.com"
                          className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                          required
                          disabled={loading}
                          autoComplete="email"
                        />
                      </div>

                      {/* Message Field */}
                      <div>
                        <label htmlFor="feedback-message" className="block text-sm font-medium text-zinc-300 mb-2">
                          Message
                        </label>
                        <textarea
                          id="feedback-message"
                          name="feedback-message"
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          placeholder="Tell us what you think..."
                          rows={4}
                          className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all resize-none"
                          required
                          disabled={loading}
                        />
                      </div>

                      {/* Submit Button */}
                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? (
                          <Fragment>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Sending...
                          </Fragment>
                        ) : (
                          <Fragment>
                            <Send className="w-4 h-4" />
                            Send Feedback
                          </Fragment>
                        )}
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </motion.div>
          </Fragment>
        )}
      </AnimatePresence>
    </Fragment>
  );
};

export default FeedbackWidget;
